<template>
  <v-card elevation="4" class="dashboard-card rounded-xl pa-5 fill-height">
    <div class="dashboard-section-title mb-4">Composición de mi cuenta de capital</div>
    <div class="capital-chart-layout">
      <div class="capital-chart-legend">
        <div v-for="item in capitalPositiveItems" :key="`legend-${item.label}`" class="capital-legend-item">
          <span class="distribution-dot" :style="{ backgroundColor: item.color }"></span>
          <span>{{ item.label }}</span>
          <strong>{{ formatMonto(item.monto) }}</strong>
        </div>
        <v-divider class="my-3"></v-divider>
        <div v-for="item in capitalNegativeItems" :key="`legend-${item.label}`" class="capital-legend-item">
          <span class="distribution-dot" :style="{ backgroundColor: item.color }"></span>
          <span>{{ item.label }}</span>
          <strong>-{{ formatMonto(Math.abs(item.monto)) }}</strong>
        </div>
      </div>

      <div class="capital-chart-area">
        <div class="capital-bars">
          <div v-for="item in capitalPositiveItems" :key="`bar-${item.label}`" class="capital-bar-row">
            <div class="capital-bar-positive" :style="{ width: item.width, backgroundColor: item.color }"></div>
          </div>
          <div class="capital-zero-line"></div>
          <div v-for="item in capitalNegativeItems" :key="`bar-${item.label}`" class="capital-bar-row capital-bar-row-negative">
            <div class="capital-bar-negative" :style="{ width: item.width, backgroundColor: item.color }"></div>
          </div>
        </div>
      </div>
    </div>
    <div class="capital-final-box mt-4">
      <div>
        <span>Saldo final de capital</span>
        <strong>{{ formatMonto(capitalResumen.saldoTotal) }}</strong>
      </div>
    </div>
  </v-card>
</template>

<script>
import conv from "@/services/conversores";

export default {
  name: "CapitalCompositionCard",
  props: {
    capitalComposicion: {
      type: Array,
      default: () => []
    },
    capitalResumen: {
      type: Object,
      default: () => ({ saldoTotal: 0 })
    }
  },
  computed: {
    capitalPositiveItems() {
      return this.capitalComposicion.filter((item) => item.monto >= 0);
    },
    capitalNegativeItems() {
      return this.capitalComposicion.filter((item) => item.monto < 0);
    }
  },
  methods: {
    formatMonto(value) {
      return conv.formatMonto(value || 0, true);
    }
  }
};
</script>

<style scoped>
.dashboard-card {
  width: 100%;
  background: #ffffff !important;
  border: 1px solid rgba(31, 45, 53, 0.08);
  box-shadow: 0 10px 28px rgba(20, 52, 57, 0.1) !important;
}

.dashboard-section-title {
  color: #20333a;
  font-size: 1.05rem;
  font-weight: 700;
}

.capital-chart-layout {
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(230px, 0.95fr) minmax(260px, 1.35fr);
}

.capital-chart-legend {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.capital-legend-item {
  align-items: center;
  color: #42565d;
  display: grid;
  gap: 10px;
  grid-template-columns: 12px minmax(110px, 1fr) 88px;
  margin-bottom: 14px;
}

.capital-legend-item span,
.capital-legend-item strong {
  font-size: 0.84rem;
}

.capital-legend-item strong {
  color: #20333a;
  font-weight: 800;
  text-align: right;
}

.distribution-dot {
  border-radius: 50%;
  height: 10px;
  width: 10px;
}

.capital-chart-area {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
}

.capital-bars {
  --capital-zero-position: 38%;
  background-image: linear-gradient(to right, rgba(37, 108, 116, 0.13) 1px, transparent 1px);
  background-size: 25% 100%;
  margin: 9px 10px 8px 18px;
  overflow: hidden;
  padding: 8px 0 8px;
  position: relative;
}

.capital-bars::before {
  background: rgba(31, 45, 53, 0.45);
  bottom: 8px;
  content: "";
  left: var(--capital-zero-position);
  position: absolute;
  top: 8px;
  width: 2px;
  z-index: 1;
}

.capital-bar-row {
  align-items: center;
  display: flex;
  height: 34px;
  margin-left: var(--capital-zero-position);
  position: relative;
  width: calc(100% - var(--capital-zero-position));
  z-index: 2;
}

.capital-bar-row-negative {
  justify-content: flex-end;
  margin-left: 0;
  transform: none;
  width: var(--capital-zero-position);
}

.capital-bar-positive,
.capital-bar-negative {
  border-radius: 2px;
  height: 15px;
}

.capital-bar-negative {
  margin-left: 0;
}

.capital-zero-line {
  border-top: 1px solid rgba(31, 45, 53, 0.22);
  height: 14px;
  margin: 2px 0 7px;
}

.capital-final-box {
  align-items: center;
  background: #f2f7ff;
  border: 1px solid rgba(37, 108, 116, 0.1);
  border-radius: 9px;
  display: grid;
  gap: 14px;
  grid-template-columns: minmax(145px, 0.8fr) 48px minmax(210px, 1.2fr);
  padding: 14px 16px;
}

.capital-final-box span {
  color: var(--v-primary-base);
  display: block;
  font-weight: 700;
}

.capital-final-box strong {
  color: var(--v-primary-base);
  display: block;
  font-size: 1.55rem;
  line-height: 1.2;
}

@media (max-width: 1263px) {
  .capital-chart-layout {
    grid-template-columns: minmax(220px, 0.9fr) minmax(260px, 1.1fr);
  }
}

@media (max-width: 959px) {
  .capital-chart-layout {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .capital-chart-legend {
    justify-content: flex-start;
  }

  .capital-chart-area {
    min-height: 140px;
  }

  .capital-final-box {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 599px) {
  .capital-legend-item {
    grid-template-columns: 10px minmax(0, 1fr);
    margin-bottom: 10px;
  }

  .capital-legend-item strong {
    grid-column: 2;
    text-align: left;
  }

  .capital-bars {
    --capital-zero-position: 42%;
    margin-left: 0;
    margin-right: 0;
  }

  .capital-final-box {
    padding: 12px;
  }

  .capital-final-box strong {
    font-size: 1.34rem;
    overflow-wrap: anywhere;
  }
}
</style>
